import { GetStaticProps } from "next"
import { useContext } from "react"
import { Box, Container, Grid, Divider } from "@mui/material"
import Icon from "@mdi/react"
import { mdiInformationOutline, mdiStairs, mdiWeightKilogram, mdiDoorOpen, mdiCash } from "@mdi/js"
import { Trans } from "next-i18next"

import { makeStyles } from "makeStyles"

import AppContext from "@secureo/common/context/AppContext/AppContext"
import Help from "containers/CategoryPage/Help"
import BreadCrumbs, { BreadCrumbAncestor } from "components/BreadCrumbs"
import Price from "components/Price"

import useTranslation from "hooks/useTranslation"

import { getServerTranslations } from "utils/i18n/getServerTranslations"
import { helpCenterBreadCrumb } from "./index"
import { flatFeesByCurrencyCode } from "@secureo/common/config/shippingZonesAndCosts"

const useStyles = makeStyles()((theme) => ({
	ul: {
		lineHeight: "120%",
		listStyle: "none",
		marginLeft: 0,
		paddingLeft: "1.7em",
		textIndent: "-1.7em",
	},
	li: {
		lineHeight: "1.5",
		marginTop: "5px",
		[theme.breakpoints.up("sm")]: {
			marginTop: "10px",
		},
	},
}))

const Delivery = () => {
	const { classes } = useStyles()
	const { t } = useTranslation("help")
	const { currencyCode } = useContext(AppContext)

	const breadCrumb: BreadCrumbAncestor = {
		label: t("delivery"),
		href: "/help/delivery",
	}

	return (
		<Box my={5}>
			<Box style={{ marginTop: "-40px", paddingBottom: "20px" }}>
				<BreadCrumbs
					breadCrumbsPaths={[
						{ ...helpCenterBreadCrumb, label: t(helpCenterBreadCrumb.label) },
					]}
					currentPath={breadCrumb}
				/>
			</Box>
			<Container>
				<Box
					mb={5}
					pb={5}
					style={{ textAlign: "center", maxWidth: "550px", margin: "auto" }}
				>
					<h1>{t("delivery")}</h1>
					<p>{t("deliveryText1")}</p>
					<p>{t("deliveryText2")}</p>
				</Box>
				<Grid container spacing={3}>
					<Grid item xs={12} sm={6}>
						<Box mb={4}>
							<div style={{ display: "flex", alignItems: "center" }}>
								<h2
									style={{
										borderBottom: "7px solid #7abfac",
										margin: "0",
										width: "50%",
									}}
								>
									{t("articlesBelow30Kg")}
								</h2>
								<div style={{ textAlign: "right", margin: "auto" }}>
									<img
										alt=""
										style={{ maxWidth: "160px" }}
										src="https://res.cloudinary.com/safehero/image/upload/v1571664483/HelpCenter/deliveryUnder35_cl6g34.png"
									/>
								</div>
							</div>
							<p style={{ color: "grey" }}>{t("articlesBelow30KgText1")}</p>
						</Box>
						<p>
							<Trans
								t={t}
								i18nKey="articlesBelow30KgText2"
								components={{
									Threshold: (
										<Price
											priceInEuroCents={
												flatFeesByCurrencyCode[currencyCode]
													.flatFeeForSmallAmountThresholdGrossCents
											}
										/>
									),
									Fee: (
										<Price
											priceInEuroCents={
												flatFeesByCurrencyCode[currencyCode]
													.flatFeeForSmallAmountCents
											}
										/>
									),
								}}
							/>
						</p>
					</Grid>
					<Grid item xs={12} sm={6}>
						<Box mb={4}>
							<div style={{ display: "flex", alignItems: "center" }}>
								<h2
									style={{
										borderBottom: "7px solid #7abfac",
										margin: "0",
										width: "50%",
									}}
								>
									{t("articlesAbove30Kg")}
								</h2>
								<div style={{ textAlign: "right", margin: "auto" }}>
									<img
										alt=""
										style={{ maxWidth: "220px" }}
										src="https://res.cloudinary.com/safehero/image/upload/v1571664484/HelpCenter/deliveryOver35_mxlovr.png"
									/>
								</div>
							</div>
							<p style={{ color: "grey" }}>{t("articlesAbove30KgText1")}</p>
						</Box>
						<p>
							<Trans t={t} i18nKey="articlesAbove30KgText2" />
						</p>
						<p>{t("articlesAbove30KgText3")}</p>
					</Grid>
					<Grid item xs={12} sm={6}>
						<Box mb={4}>
							<h2
								style={{
									borderBottom: "7px solid #7abfac",
									margin: "0",
								}}
							>
								{t("freeDeliveryOnSite")}
							</h2>
						</Box>
						<p>
							<Trans t={t} i18nKey="freeDeliveryOnSiteText1" />
						</p>
						<p>
							<Trans t={t} i18nKey="freeDeliveryOnSiteText2" />
						</p>
					</Grid>
					<Grid item xs={12} sm={6}>
						<Box mt={4} mb={4}>
							<strong>{t("freeDeliveryOnSiteNoticeHeadline")}</strong>
						</Box>
						<ul className={classes.ul}>
							<li className={classes.li}>
								<Box component="span" mr={1}>
									<Icon path={mdiStairs} size="1.3rem" color="#7abfac" />
								</Box>
								<span>{t("freeDeliveryOnSiteNotice1")}</span>
							</li>
							<li className={classes.li}>
								<Box component="span" mr={1}>
									<Icon path={mdiWeightKilogram} size="1.3rem" color="#7abfac" />
								</Box>
								<span>{t("freeDeliveryOnSiteNotice2")}</span>
							</li>
							<li className={classes.li}>
								<Box component="span" mr={1}>
									<Icon path={mdiDoorOpen} size="1.3rem" color="#7abfac" />
								</Box>
								<span>{t("freeDeliveryOnSiteNotice3")}</span>
							</li>
							<li className={classes.li}>
								<Box component="span" mr={1}>
									<Icon path={mdiCash} size="1.3rem" color="#7abfac" />
								</Box>
								<span>{t("freeDeliveryOnSiteNotice4")}</span>
							</li>
						</ul>
					</Grid>
				</Grid>
				<Box></Box>
				<Box
					style={{
						maxWidth: "350px",
						margin: "auto",
						marginTop: "50px",
						display: "flex",
						alignItems: "center",
						border: "1px solid #7ABFAC",
						background: "rgba(122, 191, 172, 0.3)",
						padding: "1px",
						borderRadius: "4px",
					}}
				>
					<div style={{ margin: "15px" }}>
						<Icon path={mdiInformationOutline} size={1} />
					</div>
					<p>{t("phoneNotice")}</p>
				</Box>
				<Box my={5} pb={5}>
					<Divider />
					<Help />
				</Box>
			</Container>
		</Box>
	)
}

export const getStaticProps: GetStaticProps = async (context) => {
	const props = {
		...(await getServerTranslations(context.locale, [
			"common",
			"countries",
			"languages",
			"help",
			"category",
		])),
	}

	return {
		props,
	}
}

export default Delivery
